import { useQueryClient } from '@tanstack/react-query';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { memo, useEffect, useState } from 'react';

import { Box, ButtonQuantity, CountDownTimer, Image, Typography } from '@core/component';
import { Translate, util } from '@core/constant';
import { useContextCurrency, useContextMediaQuery, useContextRouting } from '@core/context';
import { useLanguage, useModalStockAlert } from '@core/hook';
import { EFilenameBlur, getStaticPathImage, getStaticPathImageBlurData } from '@core/media';
import {
  EActionAttribute,
  EColor,
  EFontWeight,
  EIcon,
  ESize,
  ETimeFormat,
  ETypographyVariant,
} from '@core/type';
import { EQueryKey, ProductEntity, ProductPageEntity } from '@core/type/api';
import {
  ButtonAddToCart,
  ButtonAutoSavingsBadge,
  ModalAddStockAlert,
  ModalUpdateStockAlert,
} from '@core/widget';

import { PreorderText } from './PreorderText';
import { ProductSectionCartActionsProps } from './interface-product-show';

const ProductSectionCartActionsContainer = ({
  isRepeatBuyActive,
  productCount,
  setProductCount,
  shippingMethod,
  ...productProps
}: ProductEntity & ProductSectionCartActionsProps) => {
  const { isPhone, isMobile, mediaQuery } = useContextMediaQuery();
  const queryClient = useQueryClient();
  const { language } = useLanguage();
  const { currency } = useContextCurrency();
  const { query } = useContextRouting();
  const { addingProps, updatingProps, isUpdatingModal } = useModalStockAlert(productProps);
  const { currentStock, isRepeatBuy, preorder, deal } = productProps;
  const [seconds, setSeconds] = useState<number>(null);

  useEffect(() => {
    setSeconds(
      Math.floor(
        Math.abs(new Date(deal?.endDate).getTime() - new Date().getTime()) /
          util.MILLISECONDS_IN_SECOND,
      ),
    );
  }, [deal?.endDate]);

  const { t: tButton } = useTranslation(Translate.common.BUTTON);

  const { preorderStock, isPreorder } = preorder;

  const handleQuantityButton = (count: number) => {
    setProductCount(count);
  };

  const onDealEnd = () => {
    const productPage: ProductPageEntity = queryClient.getQueryData([
      EQueryKey.PRODUCT_SHOW,
      language,
      currency,
      query.slug,
    ]);
    queryClient.setQueryData(
      [EQueryKey.PRODUCT_SHOW, language, currency, query.slug],
      (): ProductPageEntity => {
        return { ...productPage, deal: null, indicator: { ...productPage?.indicator, deal: null } };
      },
    );
  };

  const [counterDatetime, setCounterDatetime] = useState<number>(null);
  useEffect(() => {
    setCounterDatetime(Date.now());
  }, []);

  return (
    <Box maxWidth={'450px'}>
      {deal && (
        <Box display="flex" gap="3px" alignItems="center" marginBottom={'16px'}>
          <Image
            alt={`StopwatchIcon icon`}
            width={24}
            height={24}
            style={{ objectFit: 'cover' }}
            src={getStaticPathImage('product/stopwatch.png')}
            blurDataURL={getStaticPathImageBlurData(EFilenameBlur['product/stopwatch.png'])}
          />
          <Typography variant={ETypographyVariant.BODY2} display="inline">
            <Trans
              i18nKey={`${Translate.common.PRODUCT}:buttons.dealMessage`}
              components={{
                count: (
                  <Typography
                    as={EActionAttribute.SPAN}
                    variant={ETypographyVariant.BODY2}
                    color={EColor.ACCENT}
                    fontWeight={EFontWeight.BOLD}
                  >
                    {isPreorder ? preorderStock : currentStock}
                  </Typography>
                ),
                timer: (
                  <Typography
                    as={EActionAttribute.SPAN}
                    variant={ETypographyVariant.BODY2}
                    color={EColor.ACCENT}
                    fontWeight={EFontWeight.BOLD}
                    display="inline-block"
                  >
                    <CountDownTimer
                      format={ETimeFormat.dhhmmss}
                      seconds={seconds}
                      setSeconds={setSeconds}
                      counterDatetime={counterDatetime}
                      onExpired={onDealEnd}
                    />
                  </Typography>
                ),
              }}
            />
          </Typography>
        </Box>
      )}
      {isPreorder && <PreorderText preorder={preorder} />}
      <Box display={'flex'} alignItems={'center'}>
        <ButtonQuantity
          min={1}
          max={isPreorder ? preorderStock : currentStock}
          onChange={handleQuantityButton}
          value={productCount}
          size={ESize.SM}
        />
        <Box
          marginLeft={isPhone ? '24px' : '40px'}
          maxWidth={'267px'}
          width={isMobile && mediaQuery !== ESize.XS ? '267px' : 'auto'}
          flex={'1'}
        >
          {isRepeatBuy && isRepeatBuyActive ? (
            <ButtonAutoSavingsBadge
              product={productProps}
              quantity={productCount}
              buttonProps={{ isFullWidth: true, hiddenIcon: false }}
              title={tButton('addToCart.title')}
            />
          ) : (
            <ButtonAddToCart
              product={productProps}
              productCount={productCount}
              isFullWidth
              title={tButton('addToCart.title')}
              startIcon={EIcon.PLUS}
              iconSize={ESize.SM}
              shippingMethod={shippingMethod}
            />
          )}
        </Box>
      </Box>
      {isUpdatingModal ? (
        <ModalUpdateStockAlert {...updatingProps} />
      ) : (
        <ModalAddStockAlert {...addingProps} />
      )}
    </Box>
  );
};

export const ProductCartActions = memo(ProductSectionCartActionsContainer);
